import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  FormControlLabel,
  Switch,
  Box,
  Divider,
  IconButton,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Cookie as CookieIcon,
} from '@mui/icons-material';
import './CookieModal.css';

const CookieModal = ({ open, onClose, cookieTypes, preferences, onSave }) => {
  const [localPreferences, setLocalPreferences] = useState(preferences);

  useEffect(() => {
    setLocalPreferences(preferences);
  }, [preferences]);

  const handleToggle = (cookieId) => {
    // Les cookies nécessaires ne peuvent pas être désactivés
    if (cookieId === 'necessary') return;
    
    setLocalPreferences(prev => ({
      ...prev,
      [cookieId]: !prev[cookieId]
    }));
  };

  const handleSave = () => {
    onSave(localPreferences);
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      performance: true,
      functionality: true,
      advertising: true,
    };
    setLocalPreferences(allAccepted);
    onSave(allAccepted);
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      performance: false,
      functionality: false,
      advertising: false,
    };
    setLocalPreferences(onlyNecessary);
    onSave(onlyNecessary);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      className="cookie-modal"
      PaperProps={{
        className: 'cookie-modal-paper'
      }}
    >
      <DialogTitle className="cookie-modal-title">
        <Box className="cookie-modal-header">
          <Box className="cookie-modal-title-content">
            <CookieIcon className="cookie-modal-icon" />
            <Typography variant="h5" component="div">
              Personnaliser les cookies
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            className="cookie-modal-close"
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography variant="body2" className="cookie-modal-subtitle">
          Choisissez les types de cookies que vous souhaitez autoriser.
          Vous pouvez modifier ces préférences à tout moment.
        </Typography>
      </DialogTitle>

      <DialogContent className="cookie-modal-content">
        <Box className="cookie-preferences">
          {cookieTypes.map((type) => (
            <Card key={type.id} className="cookie-preference-card">
              <CardContent>
                <Box className="cookie-preference-header">
                  <Box className="cookie-preference-info">
                    <Box
                      className="cookie-preference-icon"
                      sx={{ backgroundColor: type.color }}
                    >
                      {type.icon}
                    </Box>
                    <Box className="cookie-preference-text">
                      <Typography variant="h6" className="cookie-preference-title">
                        {type.title}
                      </Typography>
                      {type.required && (
                        <Chip
                          label="Obligatoire"
                          size="small"
                          color="error"
                          className="cookie-preference-chip"
                        />
                      )}
                    </Box>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={localPreferences[type.id]}
                        onChange={() => handleToggle(type.id)}
                        disabled={type.required}
                        color="primary"
                      />
                    }
                    label=""
                    className="cookie-preference-switch"
                  />
                </Box>
                <Typography variant="body2" className="cookie-preference-description">
                  {type.description}
                </Typography>
                <Typography variant="caption" className="cookie-preference-examples">
                  <strong>Exemples :</strong> {type.examples}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>

        <Divider className="cookie-modal-divider" />

        <Box className="cookie-modal-info">
          <Typography variant="body2" color="text.secondary">
            <strong>Note :</strong> Les cookies strictement nécessaires sont toujours activés
            car ils sont essentiels au fonctionnement du site. Vous pouvez désactiver
            les autres types de cookies, mais cela peut affecter certaines fonctionnalités.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions className="cookie-modal-actions">
        <Box className="cookie-modal-buttons">
          <Button
            onClick={handleRejectAll}
            variant="outlined"
            color="error"
            className="cookie-modal-button"
          >
            Refuser tout
          </Button>
          <Button
            onClick={handleAcceptAll}
            variant="outlined"
            color="success"
            className="cookie-modal-button"
          >
            Accepter tout
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            className="cookie-modal-button cookie-modal-button-primary"
          >
            Enregistrer mes préférences
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default CookieModal;